import {
  ActionEnum,
  ActionFactory,
  BlockchainNetwork,
  SupplyToEarnApy,
  SupplyToEarnReward,
  USDS,
  buildLiquidateIxs,
  depositReserveLiquiditySolanaProxyInstruction,
  withdrawReserveLiquiditySolanaProxyInstruction,
} from "@renec-foundation/relend-adapter"

import { connectionAtom } from "@/atoms/settings"
import { IS_MAINNET } from "@/utils/constants"
import { SendTransactionError } from "@/utils/dataTypes"
import { commonErrorResponse, stringifyVariable } from "@/utils/helpers"
import { DemonWalletAdapter } from "@renec-foundation/wallet-adapter-react"
import {
  Connection,
  PublicKey,
  Transaction,
  TransactionInstruction,
  TransactionMessage,
  VersionedTransaction,
} from "@solana/web3.js"
import BN from "bn.js"
import { useAtomValue } from "jotai"
import useTrans from "./useTrans"
import useWalletConnect from "./useWalletConnect"

export type ActionHandlerOutputType = {
  isSucceeded: boolean
  tx?: string
  error?: string
}

// Add a type for proxy instruction functions
type ProxyInstructionFn = (
  connection: Connection,
  amount: string,
  publicKey: PublicKey,
  allowedToken: PublicKey,
) => Promise<TransactionInstruction[]>

// Create a map of actions to their corresponding instruction functions
const PROXY_INSTRUCTION_MAP: Record<ActionEnum, ProxyInstructionFn | undefined> = {
  [ActionEnum.DEPOSIT]: depositReserveLiquiditySolanaProxyInstruction,
  [ActionEnum.WITHDRAW]: withdrawReserveLiquiditySolanaProxyInstruction,
  [ActionEnum.BORROW]: undefined,
  [ActionEnum.REPAY]: undefined,
  [ActionEnum.CLAIM_TO_EARN]: undefined,
} as const

const useActionOnChain = () => {
  const trans = useTrans()
  const connection = useAtomValue(connectionAtom)
  const { wallet, address } = useWalletConnect()

  const sendTx = async (transaction: Transaction) => {
    if (wallet && address) {
      const { blockhash } = await connection.getLatestBlockhash()
      transaction.recentBlockhash = blockhash
      transaction.feePayer = new PublicKey(address)
      const messageV0 = new TransactionMessage({
        payerKey: new PublicKey(address),
        recentBlockhash: blockhash,
        instructions: transaction.instructions,
      }).compileToV0Message()
      const versionedTransaction = new VersionedTransaction(messageV0)
      const result = await connection.simulateTransaction(versionedTransaction)
      if (result.value.err) {
        const logs = result.value.logs ? result.value.logs : []
        const logToThrow = logs.length > 0 ? logs : [stringifyVariable(result.value.err)]
        throw new SendTransactionError("failed to send transaction: " + logToThrow, logs)
      }
      if (wallet.adapter instanceof DemonWalletAdapter) {
        return (wallet.adapter as DemonWalletAdapter).sendTransactionWithSigners(transaction, connection, [])
      } else {
        return wallet.adapter.sendTransaction(transaction, connection)
      }
    }
    throw new Error("Wallet not found")
  }

  const actionHandler = async ({
    network,
    action,
    amount,
    symbol,
    mintAddress,
    supplyToEarnApys,
    supplyToEarnRewards,
    lendingMarketAddress,
    referralAccount,
    nftVoucherAddress,
    nftInterestAmount,
  }: {
    network: BlockchainNetwork
    action: ActionEnum
    amount: string
    symbol: string
    mintAddress?: string
    supplyToEarnApys?: SupplyToEarnApy[]
    supplyToEarnRewards?: SupplyToEarnReward[]
    lendingMarketAddress?: string
    referralAccount?: PublicKey
    nftVoucherAddress?: PublicKey
    nftInterestAmount?: string
  }): Promise<ActionHandlerOutputType> => {
    try {
      if (wallet && address) {
        let txSignature
        if (network === BlockchainNetwork.SOLANA && IS_MAINNET && symbol === USDS) {
          const proxyInstructionFn = PROXY_INSTRUCTION_MAP[action]

          if (!proxyInstructionFn) {
            throw new Error("Supply and Withdraw are the only supported actions for USDS")
          }

          if (!mintAddress) {
            throw new Error("Mint address is required")
          }

          const ixs = await proxyInstructionFn(connection, amount, new PublicKey(address), new PublicKey(mintAddress))

          const transaction = new Transaction()
          transaction.add(...ixs)
          txSignature = await sendTx(transaction)
        } else {
          const relendAction = await ActionFactory.buildTxns({
            action,
            network,
            connection,
            amount,
            symbol,
            publicKey: new PublicKey(address),
            supplyToEarnApys,
            supplyToEarnRewards,
            lendingMarketAddress,
            referralAccount,
            nftVoucherAddress,
            nftInterestAmount,
          })
          txSignature = await relendAction.sendTransactions(sendTx)
        }

        return {
          isSucceeded: true,
          tx: txSignature,
        }
      }

      throw new Error("Wallet not found")
    } catch (error: any) {
      return commonErrorResponse(error, trans)
    }
  }

  /**
   * Liquidate action: Build a transaction that allows users to liquidate obligation of other users
   */
  const liquidateObligation = async (
    network: BlockchainNetwork,
    obligation: PublicKey,
    liquidityAmount: string | BN,
    depositReserve: PublicKey,
    borrowReserve: PublicKey,
    trans: any,
  ): Promise<ActionHandlerOutputType> => {
    try {
      if (address) {
        const transaction = new Transaction()
        const ixs = await buildLiquidateIxs(
          network,
          connection,
          obligation,
          liquidityAmount,
          depositReserve,
          borrowReserve,
          new PublicKey(address),
        )

        transaction.add(...ixs)
        const txSignature = await sendTx(transaction)

        return {
          isSucceeded: true,
          tx: txSignature,
        }
      }

      throw new Error("Wallet not found")
    } catch (error: any) {
      return commonErrorResponse(error, trans)
    }
  }

  return { actionHandler, liquidateObligation }
}

export default useActionOnChain
